import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Feather, Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

import Colors from "../../Resources/Color";
import { useScreenDimensions, useDevice } from "../_layout";
import IconEmptyView from "../Components/IconEmptyView";
import PredefinedExercise from "../Firestore/PredefinedExercise";
import PredefinedExerciseLayout from "./PredefinedExerciseLayout";
import Attribute from "../Firestore/Attribute";
import { useCoachStore } from "../contexts/CoachStoreContext";
import PredefinedAttributeLayout from "./PredefinedAttributeLayout";
import { withAlpha } from "../utils/colorUtils";
import { getExerciseSearchService } from "./Search/ExerciseSearchService";
import { v4 as uuidv4 } from "uuid";

// Match selection styling used by pills in ExerciseSearchFloating.js
const PILL_COLORS = {
  exercise: {
    background: "rgba(63, 123, 141, 0.12)",
    text: Colors.bubbleColor,
    border: "rgba(63, 123, 141, 0.18)",
  },
  modifier: {
    background: "rgba(156, 156, 156, 0.12)",
    text: Colors.darkgrayColor,
    border: "rgba(156, 156, 156, 0.20)",
  },
};

const SelectedChip = ({ label, type, onRemove, preventBlur = false }) => {
  const colors = PILL_COLORS[type] || PILL_COLORS.modifier;
  const { isMobile } = useDevice();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 10,
        borderWidth: 1,
        maxWidth: "100%",
        borderColor:
          type === "exercise"
            ? Colors.bubbleColor
            : type === "modifier"
            ? Colors.darkgrayColor
            : colors.border,
        backgroundColor: colors.background,
      }}
    >
      <View style={{ flex: 1, flexShrink: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 12,
            fontWeight: "500",
            color: colors.text,
            flexShrink: 1,
          }}
        >
          {label}
        </Text>
      </View>

      <TouchableOpacity
        onPress={onRemove}
        onMouseDown={(e) => {
          if (!preventBlur) return;
          e?.preventDefault?.();
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          alignItems: "center",
          justifyContent: "center",
          outlineStyle: "none",
          cursor: "pointer",
        }}
      >
        <Feather
          name="x"
          size={14}
          color={isHovered ? Colors.redColor : Colors.grayColor}
        />
      </TouchableOpacity>
    </View>
  );
};

/**
 * ExerciseManualPickerModal
 *
 * Layout-only manual selector for:
 * - one base exercise (single-select)
 * - optional modifiers (multi-select)
 *
 * Designed to emit the same payload shape expected by:
 * - Block.addSlotFromSearchResult
 * - Block.updateSlotFromSearchResult
 *
 * onSelectExercise({
 *   exerciseId,
 *   exerciseOriginalName,
 *   exerciseName,
 *   exerciseType?,
 *   link?,
 *   modifiers: [{ id, originalName, name, type, subtype }]
 * })
 */
const ExerciseManualPickerModal = ({
  onClose,
  onSelectExercise,
  onBack,
  initialSearchQuery = "",
  block = null,
  slotExercise = null,
  primarySlotExercise = null,
  protocolSlot = null,
  currentWeek = null,
  isSecondProtocol = false,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { isMobile } = useDevice();
  const { screenSize } = useScreenDimensions();
  const coachStore = useCoachStore();

  const EXCLUSIVE_MODIFIER_TITLES = useMemo(
    () =>
      new Set([
        Attribute.AttributeTitles.equipment,
        Attribute.AttributeTitles.rom,
        Attribute.AttributeTitles.tempo,
        Attribute.AttributeTitles.stanceGrip,
      ]),
    []
  );

  // If we're editing an existing slotExercise, initialize selection from it.
  // For "add secondary" flows, SlotTextNew passes the primary slotExercise as a fallback
  // (because the secondary doesn't exist yet). In that case, do NOT preselect from primary.
  const slotExerciseForInit = useMemo(() => {
    if (!slotExercise) return null;
    if (isSecondProtocol && !slotExercise.isSecondaryProtocol) return null;
    return slotExercise;
  }, [isSecondProtocol, slotExercise]);

  const didInitFromSlotRef = useRef(false);

  const [doneHovered, setDoneHovered] = useState(false);

  const [exerciseSearchText, setExerciseSearchText] = useState("");
  const [modifierSearchText, setModifierSearchText] = useState("");

  const [isExerciseSearchFocused, setIsExerciseSearchFocused] = useState(false);
  const [isModifierSearchFocused, setIsModifierSearchFocused] = useState(false);

  // Mobile-only: keep an accurate (non-stale) record of focus state so we can
  // decide whether to restore keyboard focus after taps.
  const isExerciseSearchFocusedRef = useRef(false);
  const isModifierSearchFocusedRef = useRef(false);

  // Mobile web: keyboard can be dismissed while the input remains focused.
  // Track keyboard visibility so we can avoid re-opening it unintentionally.
  const isSoftKeyboardOpenRef = useRef(false);

  useEffect(() => {
    if (!isMobile) return;

    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;

    const update = () => {
      // Heuristic: when the keyboard is open, the visualViewport height shrinks.
      const diff = (window.innerHeight || 0) - (vv.height || 0);
      isSoftKeyboardOpenRef.current = diff > 120;
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [isMobile]);

  // Mobile-only: show exactly one list at a time.
  // - Defaults to exercises when the view appears.
  // - Focus/typing/explicit expand toggles which list is visible.
  const [mobilePane, setMobilePane] = useState("exercise");

  // Local bump to force a rerender when we mutate MobX arrays (push/splice)
  // from this component. This keeps updates immediate without making the whole
  // component an observer.
  const [exerciseListVersion, setExerciseListVersion] = useState(0);

  const exerciseSearchTextRef = useRef("");
  const modifierSearchTextRef = useRef("");

  const exerciseSearchInputRef = useRef(null);
  const modifierSearchInputRef = useRef(null);
  const prevSelectedExerciseIdRef = useRef(null);

  // Mobile-only: guard against accidental focus switches (e.g. during scrolling).
  // We arm a pane right before an intentional focus (tap/expand/programmatic focus).
  // Then, onFocus only switches panes when armed or already on that pane.
  const armedNextFocusPaneRef = useRef(null);
  const armNextFocusPane = useCallback((pane) => {
    armedNextFocusPaneRef.current = pane;
  }, []);
  const consumeArmedNextFocusPane = useCallback(() => {
    const v = armedNextFocusPaneRef.current;
    armedNextFocusPaneRef.current = null;
    return v;
  }, []);

  const focusExerciseSearch = useCallback(() => {
    if (isMobile) {
      armNextFocusPane("exercise");
    }
    setTimeout(() => {
      try {
        exerciseSearchInputRef.current?.focus?.();
      } catch {
        // no-op
      }
    }, 50);
  }, [armNextFocusPane, isMobile]);

  const focusExerciseSearchImmediate = useCallback(() => {
    if (isMobile) {
      armNextFocusPane("exercise");
    }
    try {
      exerciseSearchInputRef.current?.focus?.();
    } catch {
      // no-op
    }
  }, [armNextFocusPane, isMobile]);

  const focusModifierSearch = useCallback(() => {
    if (isMobile) {
      armNextFocusPane("modifier");
    }
    // Let the UI commit before focusing.
    setTimeout(() => {
      try {
        modifierSearchInputRef.current?.focus?.();
      } catch {
        // no-op
      }
    }, 50);
  }, [armNextFocusPane, isMobile]);

  const focusModifierSearchImmediate = useCallback(() => {
    if (isMobile) {
      armNextFocusPane("modifier");
    }
    try {
      modifierSearchInputRef.current?.focus?.();
    } catch {
      // no-op
    }
  }, [armNextFocusPane, isMobile]);

  const isEnterKeyEvent = useCallback((e) => {
    const ne = e?.nativeEvent;
    const key = ne?.key ?? e?.key;
    const keyCode = ne?.keyCode ?? ne?.which ?? e?.keyCode;
    return key === "Enter" || keyCode === 13;
  }, []);

  // IMPORTANT: Do NOT use PredefinedExercise.getPredefinedArray() here.
  // That static list is hardcoded in Spanish.
  // Use the coach-loaded predefined exercises (same as ExerciseLibrary/Search),
  // which store i18n keys and properly react to language changes.
  // NOTE: coachStore.customPredefinedExercises is a MobX observable array.
  // Mutations (push/splice/reorder) do not change the array reference, so using it
  // as a useMemo dependency would prevent recomputation. We intentionally depend on
  // its length (and local state changes) when memoizing derived rows.
  const predefinedExercises = coachStore.customPredefinedExercises || [];

  // NOTE: coachStore.customPredefinedAttributes is a MobX observable array.
  // Mutations do not change the array reference, so we depend on its length in
  // memoized selectors and force a rerender after adds.
  const predefinedAttributes = coachStore.customPredefinedAttributes || [];

  const exerciseRows = useMemo(() => {
    const order = [
      PredefinedExercise.ExerciseTypes.kneeDominant,
      PredefinedExercise.ExerciseTypes.hipDominant,
      PredefinedExercise.ExerciseTypes.horizontalPush,
      PredefinedExercise.ExerciseTypes.verticalPush,
      PredefinedExercise.ExerciseTypes.horizontalPull,
      PredefinedExercise.ExerciseTypes.verticalPull,
      PredefinedExercise.ExerciseTypes.weightlifting,
      PredefinedExercise.ExerciseTypes.other,
    ];

    const q = exerciseSearchText.trim().toLowerCase();

    return order
      .map((typeKey) => {
        const items = predefinedExercises
          .filter((ex) => ex.type === typeKey)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((ex) => {
            const nameKey = ex?.name || "";
            const originalName =
              ex?.originalName || ex?.original_name || ex?.original || "";

            return {
              id: ex.id,
              nameKey,
              name: nameKey ? t(nameKey) : "",
              originalName,
              type: ex.type,
              link: typeof ex?.link === "string" ? ex.link : "",
            };
          })
          .filter((ex) => {
            if (!q) return true;
            return (
              (ex.name || "").toLowerCase().includes(q) ||
              (ex.originalName || "").toLowerCase().includes(q)
            );
          });

        return {
          key: typeKey,
          title: typeKey,
          items,
        };
      })
      .filter(
        (row) => row.items.length > 0 || exerciseSearchText.trim() === ""
      );
  }, [
    exerciseSearchText,
    predefinedExercises.length,
    exerciseListVersion,
    t,
    language,
  ]);

  const modifierSections = useMemo(() => {
    const order = [
      {
        title: Attribute.AttributeTitles.equipment,
        subtitles: [""],
      },
      {
        title: Attribute.AttributeTitles.tempo,
        subtitles: [""],
      },
      {
        title: Attribute.AttributeTitles.rom,
        subtitles: ["", "boards-string"],
      },
      {
        title: Attribute.AttributeTitles.stanceGrip,
        subtitles: ["", "grip-string", "stance-string"],
      },
      {
        title: Attribute.AttributeTitles.loadAccomodation,
        subtitles: ["", "bands-string", "reverseBands-string"],
      },
      {
        title: Attribute.AttributeTitles.accessories,
        subtitles: [""],
      },
      {
        title: Attribute.AttributeTitles.other,
        subtitles: [""],
      },
    ];

    const q = modifierSearchText.trim().toLowerCase();

    return order
      .map(({ title, subtitles }) => {
        const byTitle = predefinedAttributes
          .filter((att) => att.title === title)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        const groups = subtitles
          .map((subtitle) => {
            const items = byTitle
              .filter((att) => (att.subtitle || "") === subtitle)
              .map((att) => {
                const translatedName = t(att.name);
                return {
                  id: att.id,
                  name: translatedName,
                  originalName: att.name,
                  type: att.title,
                  subtype: att.subtitle || null,
                };
              })
              .filter((m) => {
                if (!q) return true;
                return (
                  (m.name || "").toLowerCase().includes(q) ||
                  (m.originalName || "").toLowerCase().includes(q)
                );
              });

            return {
              key: `${title}-${subtitle || "_"}`,
              subtitle,
              items,
            };
          })
          .filter(
            (g) =>
              g.items.length > 0 ||
              modifierSearchText.trim() === "" ||
              subtitles.length === 1
          );

        return {
          key: title,
          title,
          groups,
        };
      })
      .filter(
        (section) =>
          section.groups.some((g) => g.items.length > 0) ||
          modifierSearchText.trim() === ""
      );
  }, [modifierSearchText, predefinedAttributes.length, t, language]);

  // Placeholder initial selection so the layout is visible
  const defaultExerciseId = null;
  const defaultModifierIds = useMemo(() => new Set(), []);

  const [selectedExerciseId, setSelectedExerciseId] =
    useState(defaultExerciseId);
  const [selectedModifierIds, setSelectedModifierIds] =
    useState(defaultModifierIds);

  // Local fallback registry for selections that are not present in the current
  // predefined exercises/attributes (e.g. user used an exercise previously and
  // later deleted it from the library). This is intentionally component-local
  // to avoid mutating global MobX stores.
  const fallbackExerciseByIdRef = useRef(new Map());
  const fallbackModifierByIdRef = useRef(new Map());
  const [fallbackRegistryVersion, setFallbackRegistryVersion] = useState(0);

  const bumpFallbackRegistryVersion = useCallback(() => {
    setFallbackRegistryVersion((v) => v + 1);
  }, []);

  const ensureFallbackExerciseId = useCallback(
    (originalName, link = "") => {
      const safeOriginalName = (originalName || "").trim();
      if (!safeOriginalName) {
        return null;
      }

      const id = `missing-exercise:${safeOriginalName}`;
      const map = fallbackExerciseByIdRef.current;
      if (!map.has(id)) {
        const translated = t(safeOriginalName);
        map.set(id, {
          id,
          nameKey: safeOriginalName,
          name: translated || safeOriginalName,
          originalName: safeOriginalName,
          type: "",
          link: typeof link === "string" ? link : "",
        });
        bumpFallbackRegistryVersion();
      } else if (
        typeof link === "string" &&
        map.get(id)?.link !== link
      ) {
        map.set(id, {
          ...map.get(id),
          link,
        });
        bumpFallbackRegistryVersion();
      }
      return id;
    },
    [bumpFallbackRegistryVersion, t]
  );

  const ensureFallbackModifierId = useCallback(
    (rawAttr) => {
      if (!rawAttr) {
        return null;
      }

      const originalName = (
        rawAttr.name ||
        rawAttr.originalName ||
        rawAttr.original_name ||
        ""
      ).trim();

      if (!originalName) {
        return null;
      }

      const type = (rawAttr.title || rawAttr.type || "").trim();
      const subtypeRaw = (rawAttr.subtitle || rawAttr.subtype || "").trim();
      const subtype = subtypeRaw || null;

      const id = `missing-mod:${originalName}|${type}|${subtypeRaw}`;
      const map = fallbackModifierByIdRef.current;
      if (!map.has(id)) {
        const translated = t(originalName);
        map.set(id, {
          id,
          name: translated || originalName,
          originalName,
          type,
          subtype,
        });
        bumpFallbackRegistryVersion();
      }

      return id;
    },
    [bumpFallbackRegistryVersion, t]
  );

  // If we auto-initialize an exercise from the incoming search query,
  // and the user intentionally clears it, we must not auto-select again
  // (otherwise the chip becomes "undeletable").
  const [suppressAutoInit, setSuppressAutoInit] = useState(false);

  useEffect(() => {
    setSuppressAutoInit(false);
  }, [initialSearchQuery]);

  const allExercises = useMemo(
    () => exerciseRows.flatMap((s) => s.items),
    [exerciseRows]
  );

  // IMPORTANT: The selected exercise chip must not depend on the current
  // exercise search filter. If the user types into the exercise searchbar and
  // the selected exercise is filtered out, we still need to show it as selected.
  // Resolve selection from the full (unfiltered) predefined exercises list.
  const exerciseById = useMemo(() => {
    const map = new Map();
    (predefinedExercises || []).forEach((ex) => {
      if (!ex?.id) return;

      const nameKey = ex?.name || "";
      const originalName =
        ex?.originalName || ex?.original_name || ex?.original || "";

      map.set(ex.id, {
        id: ex.id,
        nameKey,
        name: nameKey ? t(nameKey) : "",
        originalName,
        type: ex.type,
        link: typeof ex?.link === "string" ? ex.link : "",
      });
    });
    return map;
  }, [language, predefinedExercises.length, t]);

  const resolvedExerciseById = useMemo(() => {
    const map = new Map(exerciseById);
    for (const [id, ex] of fallbackExerciseByIdRef.current.entries()) {
      map.set(id, ex);
    }
    return map;
  }, [exerciseById, fallbackRegistryVersion]);
  const allModifiers = useMemo(
    () => modifierSections.flatMap((s) => s.groups.flatMap((g) => g.items)),
    [modifierSections]
  );

  const modifierById = useMemo(() => {
    const map = new Map();
    // IMPORTANT: Build from the full attributes list, not only the currently
    // visible filtered list. Otherwise, exclusivity cannot unselect a previously
    // selected modifier that isn't visible under the current search query.
    (predefinedAttributes || []).forEach((att) => {
      if (!att?.id) return;
      map.set(att.id, {
        id: att.id,
        name: t(att.name),
        originalName: att.name,
        type: att.title,
        subtype: att.subtitle || null,
      });
    });
    return map;
  }, [language, predefinedAttributes.length, t]);

  const resolvedModifierById = useMemo(() => {
    const map = new Map(modifierById);
    for (const [id, m] of fallbackModifierByIdRef.current.entries()) {
      map.set(id, m);
    }
    return map;
  }, [fallbackRegistryVersion, modifierById]);

  const exerciseByOriginalName = useMemo(() => {
    const map = new Map();
    for (const ex of exerciseById.values()) {
      const key = ex?.originalName;
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, ex);
      }
    }
    return map;
  }, [exerciseById]);

  const modifierIdByKey = useMemo(() => {
    const map = new Map();
    (predefinedAttributes || []).forEach((att) => {
      if (!att?.id) return;
      const key = `${att.name}|${att.title}|${att.subtitle || ""}`;
      map.set(key, att.id);
    });
    return map;
  }, [predefinedAttributes.length]);

  const selectedExercise = useMemo(() => {
    if (!selectedExerciseId) return null;
    return resolvedExerciseById.get(selectedExerciseId) || null;
  }, [resolvedExerciseById, selectedExerciseId]);

  // Initialize from the existing slot (exercise + modifiers)
  useEffect(() => {
    if (!slotExerciseForInit) return;
    if (didInitFromSlotRef.current) return;

    const baseOriginalName =
      slotExerciseForInit?.originalName ||
      slotExerciseForInit?.original_name ||
      "";

    const matchedExercise = baseOriginalName
      ? exerciseByOriginalName.get(baseOriginalName)
      : null;

    if (matchedExercise?.id) {
      setSelectedExerciseId(matchedExercise.id);
    } else if (baseOriginalName) {
      const missingId = ensureFallbackExerciseId(
        baseOriginalName,
        typeof slotExerciseForInit?.link === "string" ? slotExerciseForInit.link : "",
      );
      if (missingId) {
        setSelectedExerciseId(missingId);
      }
    }

    const nextModifierIds = new Set();
    (slotExerciseForInit?.attributes || []).forEach((attr) => {
      if (!attr) return;

      const attrName =
        attr.name || attr.originalName || attr.original_name || "";
      const attrTitle = attr.title || attr.type || "";
      const attrSubtitle = attr.subtitle || attr.subtype || "";

      const key = `${attrName}|${attrTitle}|${attrSubtitle}`;
      const predefinedId = modifierIdByKey.get(key);
      if (predefinedId) {
        nextModifierIds.add(predefinedId);
        return;
      }

      const missingId = ensureFallbackModifierId({
        name: attrName,
        title: attrTitle,
        subtitle: attrSubtitle,
      });
      if (missingId) {
        nextModifierIds.add(missingId);
      }
    });

    if (nextModifierIds.size > 0) {
      setSelectedModifierIds(nextModifierIds);
    }

    didInitFromSlotRef.current = true;
  }, [
    ensureFallbackExerciseId,
    ensureFallbackModifierId,
    exerciseByOriginalName,
    modifierIdByKey,
    slotExerciseForInit,
  ]);

  // If the exercise is initialized (auto) or picked (manual), jump focus to modifiers.
  // Also clear the exercise searchbar when an exercise becomes selected.
  useEffect(() => {
    const prev = prevSelectedExerciseIdRef.current;
    prevSelectedExerciseIdRef.current = selectedExerciseId;

    if (!selectedExerciseId) {
      return;
    }

    // Only clear on transition into a selected state.
    if (!prev) {
      exerciseSearchTextRef.current = "";
      setExerciseSearchText("");
    }

    focusModifierSearch();
  }, [focusModifierSearch, selectedExerciseId]);

  // When entering the view with no selected exercise (or after clearing it),
  // focus the exercise searchbar.
  useEffect(() => {
    if (isMobile) {
      return;
    }
    if (!selectedExerciseId) {
      focusExerciseSearch();
    }
  }, [focusExerciseSearch, isMobile, selectedExerciseId]);

  useEffect(() => {
    // When editing an existing slot, we initialize from slotExercise instead.
    if (slotExerciseForInit) {
      return;
    }

    const q = (initialSearchQuery || "").trim();
    if (!q) {
      return;
    }

    if (suppressAutoInit) {
      return;
    }

    // Don't override an existing manual selection.
    if (selectedExerciseId) {
      return;
    }

    // Allow preselecting from the typed query even when editing.
    // (We still won't override once the user has manually selected something.)

    try {
      const searchService = getExerciseSearchService();
      const searchResults = searchService.search(
        q,
        predefinedExercises,
        predefinedAttributes
      );

      const best = Array.isArray(searchResults) ? searchResults[0] : null;
      const second = Array.isArray(searchResults) ? searchResults[1] : null;

      const bestExerciseId = best?.exerciseId;
      const bestConfidence =
        typeof best?.confidence === "number" ? best.confidence : 0;
      const secondConfidence =
        typeof second?.confidence === "number" ? second.confidence : 0;

      if (!bestExerciseId) {
        return;
      }

      // Slightly restrictive preselect:
      // Still ONLY initializes the BASE exercise (never modifiers).
      // Requires a bit of confidence OR a clear lead vs #2.
      const alnumCount = (q.match(/[a-z0-9]/gi) || []).length;
      const bestVsSecondGap = bestConfidence - secondConfidence;

      // Avoid auto-selecting on 1-character queries like "s".
      if (alnumCount < 2) {
        return;
      }

      const isLikelyEnough =
        bestConfidence >= 0.35 ||
        (bestConfidence >= 0.25 &&
          (searchResults.length < 2 || bestVsSecondGap >= 0.05)) ||
        (alnumCount >= 4 &&
          bestConfidence >= 0.22 &&
          (searchResults.length < 2 || bestVsSecondGap >= 0.08));

      if (!isLikelyEnough) {
        return;
      }

      // If the list isn't built yet (timing), still set the id and let it resolve later.
      // But when we do have exercises loaded, ensure the inferred id actually exists.
      if (predefinedExercises.length > 0 && !exerciseById.has(bestExerciseId)) {
        return;
      }

      setSelectedExerciseId(bestExerciseId);
    } catch (e) {
      // If parsing fails for any reason, fail silently and let the user pick.
      console.warn(
        "Manual picker: failed to infer exercise from search query",
        e
      );
    }
  }, [
    exerciseById,
    initialSearchQuery,
    slotExerciseForInit,
    predefinedAttributes,
    predefinedExercises,
    selectedExerciseId,
    suppressAutoInit,
    slotExercise,
  ]);

  const selectedModifiers = useMemo(() => {
    return Array.from(selectedModifierIds || [])
      .map((id) => resolvedModifierById.get(id))
      .filter(Boolean);
  }, [resolvedModifierById, selectedModifierIds]);

  const hasAnySelection = useMemo(() => {
    return !!selectedExerciseId || (selectedModifierIds?.size || 0) > 0;
  }, [selectedExerciseId, selectedModifierIds]);

  const selectedExerciseDisplayName = useMemo(() => {
    if (!selectedExercise) {
      return "";
    }

    if (!selectedModifiers || selectedModifiers.length === 0) {
      return selectedExercise.name;
    }

    let selectedEquipment = "";
    let selectedTempo = "";
    let selectedROM = "";
    let selectedStanceGrip = "";
    let selectedLoadAccomodation = "";
    let selectedAccessories = "";
    let selectedOther = "";

    if (language === "es") {
      selectedModifiers.forEach((modifier) => {
        const modifierName = modifier.name;
        switch (modifier.type) {
          case Attribute.AttributeTitles.equipment:
            selectedEquipment = ` con ${modifierName}`;
            break;
          case Attribute.AttributeTitles.rom:
            selectedROM = ` ${modifierName}`;
            break;
          case Attribute.AttributeTitles.tempo:
            selectedTempo = ` ${modifierName}`;
            break;
          case Attribute.AttributeTitles.stanceGrip:
            selectedStanceGrip = ` ${modifierName}`;
            break;
          case Attribute.AttributeTitles.loadAccomodation:
            selectedLoadAccomodation += ` ${modifierName}`;
            break;
          case Attribute.AttributeTitles.accessories:
            selectedAccessories += ` ${modifierName}`;
            break;
          default:
            selectedOther += ` ${modifierName}`;
        }
      });

      return `${selectedExercise.name}${selectedTempo}${selectedROM}${selectedStanceGrip}${selectedLoadAccomodation}${selectedAccessories}${selectedOther}${selectedEquipment}`;
    }

    selectedModifiers.forEach((modifier) => {
      const modifierName = modifier.name;
      switch (modifier.type) {
        case Attribute.AttributeTitles.equipment:
          selectedEquipment = `${modifierName} `;
          break;
        case Attribute.AttributeTitles.rom:
          selectedROM = `${modifierName} `;
          break;
        case Attribute.AttributeTitles.tempo:
          selectedTempo = `${modifierName} `;
          break;
        case Attribute.AttributeTitles.stanceGrip:
          selectedStanceGrip = `${modifierName} `;
          break;
        case Attribute.AttributeTitles.loadAccomodation:
          selectedLoadAccomodation += `${modifierName} `;
          break;
        case Attribute.AttributeTitles.accessories:
          selectedAccessories += `${modifierName} `;
          break;
        default:
          selectedOther += `${modifierName} `;
      }
    });

    return `${selectedEquipment}${selectedTempo}${selectedROM}${selectedStanceGrip}${selectedLoadAccomodation}${selectedAccessories}${selectedOther}${selectedExercise.name}`;
  }, [selectedExercise, selectedModifiers, language]);

  const toggleModifier = useCallback(
    (modifierOrId) => {
      const modifier =
        typeof modifierOrId === "string"
          ? resolvedModifierById.get(modifierOrId)
          : modifierOrId;

      const modifierId = modifier?.id;
      if (!modifierId) {
        return;
      }

      const modifierType = modifier?.type;
      const isExclusiveType =
        !!modifierType && EXCLUSIVE_MODIFIER_TITLES.has(modifierType);

      setSelectedModifierIds((prev) => {
        const next = new Set(prev);

        // Deselect if already selected
        if (next.has(modifierId)) {
          next.delete(modifierId);
          return next;
        }

        // Enforce exclusivity: remove any other selected modifier from same title
        if (isExclusiveType) {
          Array.from(next).forEach((selectedId) => {
            const selected = resolvedModifierById.get(selectedId);
            if (selected?.type === modifierType) {
              next.delete(selectedId);
            }
          });
        }

        next.add(modifierId);
        return next;
      });
    },
    [EXCLUSIVE_MODIFIER_TITLES, resolvedModifierById]
  );

  const hoveredExerciseId = useMemo(() => {
    if (!exerciseSearchText.trim()) {
      return null;
    }
    return (
      exerciseRows.find((row) => row.items.length > 0)?.items?.[0]?.id || null
    );
  }, [exerciseRows, exerciseSearchText]);

  const hoveredModifierId = useMemo(() => {
    if (!modifierSearchText.trim()) {
      return null;
    }

    for (const section of modifierSections) {
      for (const group of section.groups) {
        const first = group.items?.[0];
        if (first?.id) {
          return first.id;
        }
      }
    }

    return null;
  }, [modifierSearchText, modifierSections]);

  const hasExerciseSearchMatches = useMemo(() => {
    return exerciseRows.some((row) => (row.items || []).length > 0);
  }, [exerciseRows]);

  const hasModifierSearchMatches = useMemo(() => {
    return modifierSections.some((section) =>
      (section.groups || []).some((g) => (g.items || []).length > 0)
    );
  }, [modifierSections]);

  const showExercisesEmptyView = useMemo(() => {
    // Covers both: (1) no data at all, (2) search yields no results.
    return !hasExerciseSearchMatches;
  }, [hasExerciseSearchMatches]);

  const showModifiersEmptyView = useMemo(() => {
    return !hasModifierSearchMatches;
  }, [hasModifierSearchMatches]);

  const showExerciseEnterHint =
    exerciseSearchText.trim().length > 0 && hasExerciseSearchMatches;

  const showModifierEnterHint =
    modifierSearchText.trim().length > 0 && hasModifierSearchMatches;

  const showExerciseList = !isMobile || mobilePane === "exercise";
  const showModifierList = !isMobile || mobilePane === "modifier";

  const selectFirstVisibleExercise = useCallback(() => {
    const q = (exerciseSearchTextRef.current || "").trim();
    if (!q) {
      return;
    }

    const first = exerciseRows.find((row) => row.items.length > 0)?.items?.[0];
    if (!first?.id) {
      return;
    }

    setSelectedExerciseId(first.id);
    exerciseSearchTextRef.current = "";
    setExerciseSearchText("");
    focusModifierSearch();
  }, [exerciseRows, exerciseSearchText, focusModifierSearch]);

  const selectFirstVisibleModifier = useCallback(() => {
    const q = (modifierSearchTextRef.current || "").trim();
    if (!q) {
      return;
    }

    if (hoveredModifierId) {
      toggleModifier(hoveredModifierId);
      modifierSearchTextRef.current = "";
      setModifierSearchText("");
      focusModifierSearch();
      return;
    }

    let first = null;
    for (const section of modifierSections) {
      for (const group of section.groups) {
        const item = group.items?.[0];
        if (item?.id) {
          first = item;
          break;
        }
      }
      if (first) break;
    }

    if (!first?.id) {
      return;
    }

    toggleModifier(first);
    modifierSearchTextRef.current = "";
    setModifierSearchText("");
    focusModifierSearch();
  }, [
    focusModifierSearch,
    hoveredModifierId,
    modifierSections,
    toggleModifier,
  ]);

  const canSubmit = !!selectedExercise;

  const handleDone = useCallback(() => {
    if (!selectedExercise) {
      return;
    }

    const payload = {
      exerciseId: selectedExercise.id,
      exerciseOriginalName: selectedExercise.originalName,
      exerciseName: selectedExerciseDisplayName || selectedExercise.name,
      exerciseType: selectedExercise.type || "",
      link: typeof selectedExercise?.link === "string" ? selectedExercise.link : "",
      modifiers: selectedModifiers.map((m) => ({
        id: m.id,
        originalName: m.originalName,
        name: m.name,
        type: m.type,
        subtype: m.subtype || null,
      })),
    };

    // Save logic mirrors SlotTextNew.handleSelectExercise
    // If we have the required context, persist immediately here.
    // Otherwise, fall back to the parent callback (keeps reusability).
    if (block && (protocolSlot != null || slotExercise != null)) {
      try {
        if (slotExercise) {
          if (isSecondProtocol) {
            const targetSecond =
              primarySlotExercise?.getSecondProtocol || slotExercise;

            if (primarySlotExercise && !primarySlotExercise.getSecondProtocol) {
              // Create a new second protocol on the primary exercise
              block.addSlotFromSearchResult(
                payload,
                t,
                protocolSlot,
                currentWeek,
                true,
                primarySlotExercise
              );
            } else {
              // Update existing second protocol
              block.updateSlotFromSearchResult(targetSecond, payload, t, true);
            }
          } else {
            // Update existing primary slot exercise
            block.updateSlotFromSearchResult(slotExercise, payload, t, false);
          }
        } else {
          // Create new primary slot
          block.addSlotFromSearchResult(
            payload,
            t,
            protocolSlot,
            currentWeek,
            false,
            null
          );
        }
      } catch (e) {
        console.error("Error saving manual exercise selection:", e);
        onSelectExercise?.(payload);
      }
    } else {
      onSelectExercise?.(payload);
    }

    onClose?.();
  }, [
    block,
    canSubmit,
    currentWeek,
    isSecondProtocol,
    onClose,
    onSelectExercise,
    primarySlotExercise,
    protocolSlot,
    selectedExercise,
    selectedExerciseDisplayName,
    selectedModifiers,
    slotExercise,
    t,
  ]);

  const headerTitle = selectedExercise
    ? selectedExerciseDisplayName || selectedExercise.name
    : t("selectExercise-string");

  // Track whether we're in "create new exercise/modifier" mode so Escape cancels that
  // (instead of triggering the modal's back behavior / swallowing Escape).
  const cancelCreateExerciseRef = useRef(null);
  const cancelCreateModifierRef = useRef(null);

  // Keyboard shortcut: Escape goes back ONLY if nothing is selected.
  // If there is a selection, swallow Escape so the user doesn't accidentally lose it.
  useEffect(() => {
    if (isMobile) {
      return;
    }

    const onKeyDown = (e) => {
      if (e?.key !== "Escape") {
        return;
      }

      // If the user is currently creating a new exercise/modifier, Escape should cancel that.
      // We still swallow Escape to prevent the floating container from also handling it.
      const cancelCreateExercise = cancelCreateExerciseRef.current;
      const cancelCreateModifier = cancelCreateModifierRef.current;
      if (cancelCreateExercise || cancelCreateModifier) {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        try {
          cancelCreateExercise?.();
        } catch {
          // no-op
        }
        try {
          cancelCreateModifier?.();
        } catch {
          // no-op
        }
        return;
      }

      // Always prevent bubbling (the floating container also listens for Escape).
      e?.preventDefault?.();
      e?.stopPropagation?.();

      if (!hasAnySelection) {
        onBack?.();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [hasAnySelection, isMobile, onBack]);

  return (
    <View
      style={{
        flex: 1,
        minHeight: 0,
        backgroundColor: Colors.whiteColor,
      }}
    >
      {/* Header */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          paddingVertical: 10,
          paddingHorizontal: 12,
          borderBottomWidth: 0.5,
          borderBottomColor: Colors.borderColor,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            flex: 1,
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <TouchableOpacity
            onPress={onBack}
            disabled={!onBack}
            style={{
              paddingHorizontal: 2,
              paddingVertical: 6,
              alignItems: "center",
              justifyContent: "center",
              outlineStyle: "none",
              opacity: onBack ? 1 : 0,
              cursor: "pointer",
            }}
          >
            <Ionicons
              name="chevron-back"
              size={18}
              color={
                selectedExercise || selectedModifiers.length > 0
                  ? Colors.grayColor
                  : Colors.darkgrayColor
              }
            />
          </TouchableOpacity>

          <View style={{ flex: 1, flexShrink: 1, minWidth: 0 }}>
            <Text
              numberOfLines={2}
              ellipsizeMode="tail"
              style={{
                fontSize: 14,
                fontWeight: "700",
                color: Colors.blackColor,
                lineHeight: 16,
              }}
            >
              {headerTitle}
            </Text>
          </View>
        </View>

        <TouchableOpacity
          onPress={() => {
            if (!canSubmit) return;
            handleDone();
          }}
          onMouseEnter={() => !isMobile && canSubmit && setDoneHovered(true)}
          onMouseLeave={() => setDoneHovered(false)}
          style={{
            flexShrink: 0,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: canSubmit ? Colors.bubbleColor : Colors.grayColor,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: canSubmit
              ? doneHovered
                ? withAlpha(Colors.bubbleColor, 0.86)
                : Colors.bubbleColor
              : Colors.grayColor,
            outlineStyle: "none",
            paddingHorizontal: 16,
            height: 32,
          }}
        >
          <Text
            style={{
              fontSize: 12,
              fontWeight: "500",
              color: Colors.whiteColor,
            }}
          >
            {t("save-string")}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Selection row */}
      {(selectedExercise || selectedModifiers.length > 0) && (
        <View
          style={{
            paddingVertical: 10,
            paddingHorizontal: 12,
            flexDirection: isMobile ? "column" : "row",
            gap: 10,
            alignItems: isMobile ? "stretch" : "flex-start",
            justifyContent: "space-between",
          }}
        >
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps={isMobile ? "always" : undefined}
            keyboardDismissMode={isMobile ? "none" : undefined}
            style={{
              flex: 1,
              minHeight: 32,
              // Mobile: cap the selected chips to ~2 rows max.
              maxHeight: isMobile ? 72 : 96,
            }}
            contentContainerStyle={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            {selectedExercise && (
              <SelectedChip
                key={selectedExercise.id}
                label={selectedExercise.name}
                type="exercise"
                preventBlur={!!isMobile}
                onRemove={() => {
                  setSelectedExerciseId(defaultExerciseId);
                  if ((initialSearchQuery || "").trim()) {
                    setSuppressAutoInit(true);
                  }

                  if (isMobile) {
                    // Do not reopen keyboard. Only keep it if the search is already focused.
                    if (
                      isModifierSearchFocusedRef.current &&
                      isSoftKeyboardOpenRef.current
                    ) {
                      focusModifierSearchImmediate();
                    } else if (
                      isExerciseSearchFocusedRef.current &&
                      isSoftKeyboardOpenRef.current
                    ) {
                      focusExerciseSearchImmediate();
                    }
                  }
                }}
              />
            )}

            {selectedModifiers.map((m) => (
              <SelectedChip
                key={m.id}
                label={m.name}
                type="modifier"
                preventBlur={!!isMobile}
                onRemove={() => {
                  toggleModifier(m);

                  if (isMobile) {
                    // Prefer keeping focus in modifiers when modifying modifiers.
                    setMobilePane("modifier");

                    // Do not reopen keyboard. Only keep it if modifier search is focused.
                    if (
                      isModifierSearchFocusedRef.current &&
                      isSoftKeyboardOpenRef.current
                    ) {
                      focusModifierSearchImmediate();
                    }
                  }
                }}
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Body */}
      {(() => {
        const renderExerciseSearchHeader = (opts = {}) => {
          const withBottomBorder = !!opts.withBottomBorder;

          return (
            <View
              style={{
                paddingTop: 10,
                paddingBottom: 16,
                paddingHorizontal: 12,
                backgroundColor: Colors.whiteColor,
                position: "relative",
                borderBottomWidth: withBottomBorder ? 0.5 : 0,
                borderBottomColor: withBottomBorder
                  ? Colors.borderColor
                  : "transparent",
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  borderRadius: 12,
                  borderWidth: 0.5,
                  borderColor: Colors.borderColor,
                  backgroundColor: Colors.lightGrayColor,
                }}
              >
                <Ionicons name="search" size={14} color={Colors.grayColor} />
                <TextInput
                  ref={exerciseSearchInputRef}
                  value={exerciseSearchText}
                  onTouchStart={() => {
                    if (!isMobile) return;
                    armNextFocusPane("exercise");
                  }}
                  onMouseDown={() => {
                    if (!isMobile) return;
                    armNextFocusPane("exercise");
                  }}
                  onChangeText={(text) => {
                    if (isMobile) setMobilePane("exercise");
                    exerciseSearchTextRef.current = text;
                    setExerciseSearchText(text);
                  }}
                  onFocus={() => {
                    if (!isMobile) return;

                    const armed = consumeArmedNextFocusPane();
                    if (mobilePane !== "exercise" && armed !== "exercise") {
                      setTimeout(() => {
                        try {
                          exerciseSearchInputRef.current?.blur?.();
                        } catch {
                          // no-op
                        }
                      }, 0);
                      return;
                    }

                    setMobilePane("exercise");
                    setIsExerciseSearchFocused(true);
                    isExerciseSearchFocusedRef.current = true;
                    setIsModifierSearchFocused(false);
                    isModifierSearchFocusedRef.current = false;
                  }}
                  onBlur={() => {
                    if (!isMobile) return;
                    setIsExerciseSearchFocused(false);
                    isExerciseSearchFocusedRef.current = false;
                  }}
                  placeholder={t("searchExercise-string")}
                  placeholderTextColor={Colors.grayColor}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    flexShrink: 1,
                    fontSize: isMobile ? 16 : 12.5,
                    color: Colors.blackColor,
                    paddingVertical: 0,
                    outlineStyle: "none",
                  }}
                  autoCorrect={false}
                  autoCapitalize="none"
                  blurOnSubmit={false}
                  onSubmitEditing={selectFirstVisibleExercise}
                  onKeyPress={(e) => {
                    if (isEnterKeyEvent(e)) {
                      selectFirstVisibleExercise();
                    }
                  }}
                />

                {isMobile && (
                  <View
                    style={{
                      width: 26,
                      height: 18,
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <TouchableOpacity
                      onPress={() => {
                        // Expand exercises list; unfocus modifiers if it was focused.
                        try {
                          modifierSearchInputRef.current?.blur?.();
                        } catch {
                          // no-op
                        }
                        setIsModifierSearchFocused(false);
                        isModifierSearchFocusedRef.current = false;
                        setMobilePane("exercise");
                      }}
                      disabled={
                        isExerciseSearchFocused || mobilePane === "exercise"
                      }
                      style={{
                        width: 26,
                        height: 18,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 10,
                        outlineStyle: "none",
                        cursor:
                          isExerciseSearchFocused || mobilePane === "exercise"
                            ? "default"
                            : "pointer",
                        opacity:
                          isExerciseSearchFocused || mobilePane === "exercise"
                            ? 0
                            : 1,
                        pointerEvents:
                          isExerciseSearchFocused || mobilePane === "exercise"
                            ? "none"
                            : "auto",
                      }}
                    >
                      <Feather
                        name="maximize-2"
                        size={14}
                        color={Colors.grayColor}
                      />
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              <Text
                pointerEvents="none"
                numberOfLines={1}
                style={{
                  position: "absolute",
                  right: 14,
                  bottom: 3,
                  fontSize: 9,
                  color: Colors.grayColor,
                  opacity: showExerciseEnterHint ? 1 : 0,
                  transitionProperty: "opacity",
                  transitionDuration: "160ms",
                  transitionTimingFunction: "ease",
                }}
              >
                {t("enterSelectsFirstHint-string")}
              </Text>
            </View>
          );
        };

        const renderModifierSearchHeader = (opts = {}) => {
          const withTopBorder = !!opts.withTopBorder;

          return (
            <View
              style={{
                paddingTop: 10,
                paddingBottom: 16,
                paddingHorizontal: 12,
                backgroundColor: Colors.whiteColor,
                position: "relative",
                borderTopWidth: withTopBorder ? 0.5 : 0,
                borderTopColor: withTopBorder
                  ? Colors.borderColor
                  : "transparent",
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  borderRadius: 12,
                  borderWidth: 0.5,
                  borderColor: Colors.borderColor,
                  backgroundColor: Colors.lightGrayColor,
                }}
              >
                <Ionicons name="search" size={14} color={Colors.grayColor} />
                <TextInput
                  ref={modifierSearchInputRef}
                  value={modifierSearchText}
                  onTouchStart={() => {
                    if (!isMobile) return;
                    armNextFocusPane("modifier");
                  }}
                  onMouseDown={() => {
                    if (!isMobile) return;
                    armNextFocusPane("modifier");
                  }}
                  onChangeText={(text) => {
                    if (isMobile) setMobilePane("modifier");
                    modifierSearchTextRef.current = text;
                    setModifierSearchText(text);
                  }}
                  onFocus={() => {
                    if (!isMobile) return;

                    const armed = consumeArmedNextFocusPane();
                    if (mobilePane !== "modifier" && armed !== "modifier") {
                      setTimeout(() => {
                        try {
                          modifierSearchInputRef.current?.blur?.();
                        } catch {
                          // no-op
                        }
                      }, 0);
                      return;
                    }

                    setMobilePane("modifier");
                    setIsModifierSearchFocused(true);
                    isModifierSearchFocusedRef.current = true;
                    setIsExerciseSearchFocused(false);
                    isExerciseSearchFocusedRef.current = false;
                  }}
                  onBlur={() => {
                    if (!isMobile) return;
                    setIsModifierSearchFocused(false);
                    isModifierSearchFocusedRef.current = false;
                  }}
                  placeholder={t("searchAttributes-string")}
                  placeholderTextColor={Colors.grayColor}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    flexShrink: 1,
                    fontSize: isMobile ? 16 : 12.5,
                    color: Colors.blackColor,
                    paddingVertical: 0,
                    outlineStyle: "none",
                  }}
                  autoCorrect={false}
                  autoCapitalize="none"
                  blurOnSubmit={false}
                  onSubmitEditing={selectFirstVisibleModifier}
                  onKeyDown={(e) => {
                    if (isEnterKeyEvent(e)) {
                      e?.preventDefault?.();
                      selectFirstVisibleModifier();
                    }
                  }}
                  onKeyPress={(e) => {
                    // Fallback for platforms where onKeyDown isn't dispatched.
                    if (isEnterKeyEvent(e)) {
                      e?.preventDefault?.();
                      selectFirstVisibleModifier();
                    }
                  }}
                />

                {isMobile && (
                  <View
                    style={{
                      width: 26,
                      height: 18,
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <TouchableOpacity
                      onPress={() => {
                        // Expand modifiers list; unfocus exercises if it was focused.
                        try {
                          exerciseSearchInputRef.current?.blur?.();
                        } catch {
                          // no-op
                        }
                        setIsExerciseSearchFocused(false);
                        isExerciseSearchFocusedRef.current = false;
                        setMobilePane("modifier");
                      }}
                      disabled={
                        isModifierSearchFocused || mobilePane === "modifier"
                      }
                      style={{
                        width: 26,
                        height: 18,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 10,
                        outlineStyle: "none",
                        cursor:
                          isModifierSearchFocused || mobilePane === "modifier"
                            ? "default"
                            : "pointer",
                        opacity:
                          isModifierSearchFocused || mobilePane === "modifier"
                            ? 0
                            : 1,
                        pointerEvents:
                          isModifierSearchFocused || mobilePane === "modifier"
                            ? "none"
                            : "auto",
                      }}
                    >
                      <Feather
                        name="maximize-2"
                        size={14}
                        color={Colors.grayColor}
                      />
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              <Text
                pointerEvents="none"
                numberOfLines={1}
                style={{
                  position: "absolute",
                  right: 14,
                  bottom: 3,
                  fontSize: 9,
                  color: Colors.grayColor,
                  opacity: showModifierEnterHint ? 1 : 0,
                  transitionProperty: "opacity",
                  transitionDuration: "160ms",
                  transitionTimingFunction: "ease",
                }}
              >
                {t("enterSelectsFirstHint-string")}
              </Text>
            </View>
          );
        };

        const renderExerciseList = () => {
          if (!showExerciseList) return null;

          return (
            <ScrollView
              keyboardShouldPersistTaps={isMobile ? "always" : undefined}
              keyboardDismissMode={isMobile ? "none" : undefined}
              style={{
                flex: 1,
                borderTopWidth: 1,
                borderTopColor: Colors.borderColor,
                marginLeft: 12,
              }}
              contentContainerStyle={{
                paddingBottom: 12,
                paddingTop: 8,
                paddingRight: 12,
              }}
            >
              {showExercisesEmptyView ? (
                <IconEmptyView
                  iconLibrary={"MaterialCommunityIcons"}
                  mainText={"emptyExercisesTitle-string"}
                  subText={"noExercisesFoundText-string"}
                  iconName={"magnify-plus-outline"}
                  marginTop={32}
                  marginBottom={16}
                />
              ) : (
                <PredefinedExerciseLayout
                  rows={exerciseRows}
                  selectedExerciseId={selectedExerciseId}
                  hoveredExerciseId={hoveredExerciseId}
                  onCreateModeChange={(state) => {
                    cancelCreateExerciseRef.current = state?.isCreating
                      ? state?.cancel
                      : null;
                  }}
                  onSelectExercise={(exercise) => {
                    setSelectedExerciseId(exercise.id);
                    exerciseSearchTextRef.current = "";
                    setExerciseSearchText("");
                    // After actually selecting an exercise, move focus to modifiers.
                    if (isMobile) {
                      setMobilePane("modifier");
                      focusModifierSearch();
                    } else {
                      focusModifierSearch();
                    }
                  }}
                  onDeselectExercise={() => {
                    setSelectedExerciseId(defaultExerciseId);
                    if ((initialSearchQuery || "").trim()) {
                      setSuppressAutoInit(true);
                    }
                  }}
                  onAddExercise={(row, name) => {
                    const trimmedName = (name || "").trim();
                    if (!trimmedName) return;

                    const typeKey = row?.key || row?.title;
                    if (!typeKey) return;

                    const exercises = Array.isArray(
                      coachStore.customPredefinedExercises
                    )
                      ? coachStore.customPredefinedExercises
                      : [];

                    const highestOrder = exercises
                      .filter((item) => item.type === typeKey)
                      .reduce(
                        (maxOrder, item) => Math.max(maxOrder, item.order),
                        0
                      );

                    const newPredefinedExercise = new PredefinedExercise(
                      uuidv4(),
                      trimmedName,
                      trimmedName,
                      highestOrder ? highestOrder + 1 : 1000,
                      typeKey,
                      "",
                      "",
                      false,
                      false,
                      false,
                      0,
                      0,
                      0,
                      0,
                      0,
                      []
                    );

                    if (!Array.isArray(coachStore.customPredefinedExercises)) {
                      coachStore.customPredefinedExercises = [];
                    }

                    coachStore.customPredefinedExercises.push(
                      newPredefinedExercise
                    );
                    coachStore.updateCoachTransactionExercises(
                      newPredefinedExercise.id
                    );
                    coachStore.startOrResetBatchTimer();

                    setExerciseListVersion((v) => v + 1);

                    if (!selectedExerciseId) {
                      setSelectedExerciseId(newPredefinedExercise.id);
                      exerciseSearchTextRef.current = "";
                      setExerciseSearchText("");
                      focusModifierSearch();
                    }
                  }}
                />
              )}
            </ScrollView>
          );
        };

        const renderModifierList = () => {
          if (!showModifierList) return null;

          return (
            <ScrollView
              keyboardShouldPersistTaps={isMobile ? "always" : undefined}
              keyboardDismissMode={isMobile ? "none" : undefined}
              style={{
                flex: 1,
                borderTopWidth: 1,
                marginLeft: 12,
                borderTopColor: Colors.borderColor,
              }}
              contentContainerStyle={{
                paddingTop: 8,
                paddingBottom: 12,
                paddingRight: 12,
              }}
            >
              {showModifiersEmptyView ? (
                <IconEmptyView
                  iconLibrary={"MaterialCommunityIcons"}
                  mainText={"emptyAttributesTitle-string"}
                  subText={"noAttributesFoundText-string"}
                  iconName={"magnify-plus-outline"}
                  marginTop={32}
                  marginBottom={16}
                />
              ) : (
                <PredefinedAttributeLayout
                  sections={modifierSections}
                  selectedModifierIds={selectedModifierIds}
                  hoveredModifierId={hoveredModifierId}
                  onCreateModeChange={(state) => {
                    cancelCreateModifierRef.current = state?.isCreating
                      ? state?.cancel
                      : null;
                  }}
                  preventKeyboardDismissOnPress={
                    !!isMobile &&
                    !!isModifierSearchFocused &&
                    !!isSoftKeyboardOpenRef.current
                  }
                  onToggleModifier={(modifier) => {
                    if (
                      isMobile &&
                      isModifierSearchFocusedRef.current &&
                      !isSoftKeyboardOpenRef.current
                    ) {
                      // Input is technically focused but the keyboard was dismissed.
                      // Blur it so this tap doesn't reopen the keyboard.
                      try {
                        modifierSearchInputRef.current?.blur?.();
                      } catch {
                        // no-op
                      }
                      setIsModifierSearchFocused(false);
                      isModifierSearchFocusedRef.current = false;
                    }

                    const wasSelected = selectedModifierIds?.has?.(
                      modifier?.id
                    );
                    toggleModifier(modifier);
                    if (!wasSelected) {
                      modifierSearchTextRef.current = "";
                      setModifierSearchText("");
                      if (!isMobile) {
                        focusModifierSearch();
                      }
                    }

                    if (isMobile) {
                      setMobilePane("modifier");
                    }
                  }}
                  onAddModifier={(section, group, name) => {
                    const trimmedName = (name || "").trim();
                    if (!trimmedName) return;

                    const titleKey = section?.title || section?.key;
                    const subtitleKey = group?.subtitle || "";
                    if (!titleKey) return;

                    const attributes = Array.isArray(
                      coachStore.customPredefinedAttributes
                    )
                      ? coachStore.customPredefinedAttributes
                      : [];

                    const highestOrder = attributes
                      .filter(
                        (item) =>
                          item.title === titleKey &&
                          (item.subtitle || "") === (subtitleKey || "")
                      )
                      .reduce(
                        (maxOrder, item) => Math.max(maxOrder, item.order),
                        0
                      );

                    const newPredefinedAttribute = new Attribute(
                      uuidv4(),
                      trimmedName,
                      titleKey,
                      subtitleKey,
                      highestOrder ? highestOrder + 1 : 1000,
                      true,
                      false,
                      null,
                      null
                    );

                    if (!Array.isArray(coachStore.customPredefinedAttributes)) {
                      coachStore.customPredefinedAttributes = [];
                    }

                    coachStore.customPredefinedAttributes.push(
                      newPredefinedAttribute
                    );
                    coachStore.updateCoachTransactionAttributes(
                      newPredefinedAttribute.id
                    );
                    coachStore.startOrResetBatchTimer();

                    setSelectedModifierIds((prev) => new Set(prev));
                  }}
                />
              )}
            </ScrollView>
          );
        };

        if (isMobile) {
          // Mobile UX: keep the original stacked order (exercise on top, modifiers at bottom)
          // and only hide/show each list area based on focus/query.
          return (
            <View style={{ flex: 1, minHeight: 0 }}>
              {renderExerciseSearchHeader({ withBottomBorder: false })}
              {showExerciseList && (
                <View style={{ flex: 1, minHeight: 0 }}>
                  {renderExerciseList()}
                </View>
              )}

              {renderModifierSearchHeader({ withTopBorder: true })}
              {showModifierList && (
                <View style={{ flex: 1, minHeight: 0 }}>
                  {renderModifierList()}
                </View>
              )}
            </View>
          );
        }

        return (
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              minHeight: 0,
              gap: 12,
            }}
          >
            <View style={{ flex: 1, minHeight: 0 }}>
              {renderExerciseSearchHeader({ withBottomBorder: false })}
              {renderExerciseList()}
            </View>

            <View style={{ flex: 1, minHeight: 0 }}>
              {renderModifierSearchHeader({ withTopBorder: false })}
              {renderModifierList()}
            </View>
          </View>
        );
      })()}
    </View>
  );
};

export default ExerciseManualPickerModal;
